import { DataTable } from "@/components/ui/table/DataTable";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import { displayValue } from "@/utils/Helper";
import { Checkbox } from "@/components/ui/CheckBox";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { useToast } from "@/components/ui/UseToast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { ErrorType } from "@/app/types";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { DataTableRowActions } from "./DataTableRowAction";
import { Form } from "@/app/types/form";
import { useGetFormsQuery, useChangeFormStatusMutation, useDuplicateFormMutation } from "@/services/FormBuilderApi";
import { Badge } from "@/components/ui/Badge";
import { FormStatus } from "@/app/shared_enums/enums";
import { FormStatusFilter } from "@/constants/form-builder";
import { Button } from "@/components/ui/Button";
import { format } from "date-fns";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "created_at",
};
const FormTable = () => {
  const form = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).form ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const { toast } = useToast();
  const [openFilter, setOpenFilter] = React.useState(false);
  const [filterData, setFilterData] = React.useState({});
  const [inputValue, setInputValue] = React.useState<string>("");
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);
  const [changeStatus] = useChangeFormStatusMutation();
  const [duplicateForm] = useDuplicateFormMutation();

  const handleDuplicateForm = async (id: any) => {
    const { data } = await duplicateForm(id);
    if (data) {
      toast({
        variant: "success",
        title: "Success",
        description: "Form duplicated successfully",
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Form duplication failed",
      });
    }
  };

  const {
    data: formData,
    isLoading: isFormDataLoading,
    refetch,
    error,
    isError,
  } = useGetFormsQuery(
    {
      query: query,
    },
    {
      skip: query == "",
    }
  );

  const tableData = React.useMemo(() => {
    return Array.isArray(formData?.data) ? formData?.data : [];
  }, [formData]);

  const actionsColumn: ColumnDef<Form> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={form}
        data={row.original}
        refetch={refetch}
        changeStatus={changeStatus}
        duplicateForm={handleDuplicateForm}
        handleEdit={() => {
          window.location.href = `/admin/form-builder?slug=${row.original.slug}`;
        }}
      />
    ),
  };

  const columns: ColumnDef<Form>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      meta: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Form Name"
          sortKey="name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const Value = row?.original.name;

        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {
              <button
                onClick={() => { window.location.href = `/form/${row.original.slug}` }}
                className="underline"
                title="View Form"
              >
                {displayValue(Value)}
              </button>
            }
          </div>
        );
      },
    },
    {
      accessorKey: "submission_count",
      meta: "submission_count",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Submission Count"
          sortKey="submission_count"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
          sortable={false}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {
              <button
                onClick={() => { window.location.href = `/admin/form-submissions?id=${row.original.id}` }}
                className="underline"
              >
                {displayValue(row?.original.submission_count)}
              </button>
            }
          </div>
        );
      },
    },
    {
      accessorKey: "linked_service_count",
      meta: "linked_service_count",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Linked Service Count"
          sortKey="linked_service_count"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
          sortable={false}
        />
      ),
      cell: ({ row }) => {
        const services = row.original.linked_services || [];
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {services.length > 0 ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-pointer underline decoration-dotted">
                      {displayValue(row?.original.linked_service_count)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1">
                      <p className="font-semibold border-b border-gray-200 pb-1 mb-1">Linked Services</p>
                      {services.map((name, i) => (
                        <p key={i} className="text-xs">• {name}</p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              displayValue(row?.original.linked_service_count)
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "linked_workflow_count",
      meta: "linked_workflow_count",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Linked workflow Count"
          sortKey="linked_workflow_count"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
          sortable={false}
        />
      ),
      cell: ({ row }) => {
        const workflows = row.original.linked_workflows || [];
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {workflows.length > 0 ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-pointer underline decoration-dotted">
                      {displayValue(row?.original.linked_workflow_count)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1">
                      <p className="font-semibold border-b border-gray-200 pb-1 mb-1">Linked Workflows</p>
                      {workflows.map((name, i) => (
                        <p key={i} className="text-xs">• {name}</p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              displayValue(row?.original.linked_workflow_count)
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created At"
          sortKey="created_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("created_at") as string;
        return date ? format(new Date(date), "yyyy-MM-dd") : "-";
      },
    },
    {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <Badge
              className={`${row.original?.status == FormStatus.Published
                ? "bg-green-600"
                : row.original?.status == FormStatus.Draft
                  ? "bg-gray-500"
                  : "bg-red-500"
                } text-white font-medium`}
            >
              {displayValue(row?.original.status)}
            </Badge>
          </div>
        );
      },
    },
    ...(form !== "read" ? [actionsColumn] : []),
  ];
  const totalRecords = formData?.filtered_count ?? 0;

  React.useEffect(() => {
    if (isError) {
      // error state
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ??
          (typedError?.data as unknown as { message?: string })?.message
          }`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Form Status",
      options: FormStatusFilter,
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  return (
    <>
      <div className="flex flex-row lg:items-center justify-between gap-4 px-3 ">
        {/* Table Filter component */}
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          placeHolder={"Search"}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <Button
            access={form !== "read"}
            className="bg-primary text-sm  text-white flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={() => { window.location.href = `/admin/form-builder` }}
          >
            <i className="fa-solid fa-plus" />
            {""}
            Create New
          </Button>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""
              }`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""
                      }`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      {/* Data Table */}
      <div className="mt-4 bg-white">
        <DataTable
          columns={columns}
          data={tableData}
          isLoading={isFormDataLoading}
          searchMessage="No Form Found"
        />{" "}
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        setInputValue={setInputValue}
        filterDisplay={filterDisplay}
      />
    </>
  );
};

export default FormTable;
